import { PLAN_FACTS, planName } from '@/shared/config/plans';
import { formatCount, formatMoney, formatMonthDay } from '@/shared/lib/format';
import type { PaidPlanId, PlanChangePreviewData } from '../types';

export type SummaryRow = { key: string; label: string; value: string; isTotal: boolean };

const included = (plan: PaidPlanId): string =>
  `${formatCount(PLAN_FACTS[plan].includedPodHours)} Pod-hours a month`;

/** The Checkout summary for a new plan (design: Checkout, plans). */
export function checkoutRows(plan: PaidPlanId): SummaryRow[] {
  const facts = PLAN_FACTS[plan];
  return [
    { key: 'plan', label: 'Plan', value: `${facts.name}, billed monthly`, isTotal: false },
    { key: 'included', label: 'Included', value: included(plan), isTotal: false },
    {
      key: 'extra',
      label: 'Extra Pod-hours',
      value: `${formatMoney(facts.extraPodHourCents ?? 0)} each, on the next invoice`,
      isTotal: false,
    },
    {
      key: 'due',
      label: 'Due today',
      value: `${formatMoney(facts.priceCents)} plus tax`,
      isTotal: true,
    },
  ];
}

/** The summary for a plan change, from Paddle's preview (design: Checkout, change). */
export function changeRows(
  plan: PaidPlanId,
  preview: PlanChangePreviewData,
  daysLeft: number,
): SummaryRow[] {
  const next = preview.nextBilledAt ? formatMonthDay(preview.nextBilledAt) : 'the next period';
  const today = preview.isUpgrade
    ? {
        label: `Today, for the ${daysLeft} days left`,
        value: `${formatMoney(preview.dueTodayCents, preview.currency)} plus tax, prorated`,
      }
    : { label: 'Today', value: 'Nothing, the change waits for the next period' };
  return [
    { key: 'plan', label: 'New plan', value: `${planName(plan)}, billed monthly`, isTotal: false },
    { key: 'included', label: 'Included', value: included(plan), isTotal: false },
    { key: 'today', label: today.label, value: today.value, isTotal: false },
    {
      key: 'next',
      label: `From ${next}`,
      value: `${formatMoney(preview.nextAmountCents, preview.currency)} a month`,
      isTotal: true,
    },
  ];
}

export const isUpgrade = (from: PaidPlanId, to: PaidPlanId): boolean =>
  PLAN_FACTS[to].priceCents > PLAN_FACTS[from].priceCents;
